/**
 * The reserved attribute names, on a person and on an object.
 *
 * These are the names Customer.io owns rather than the customer: the
 * identifiers, the timestamps it maintains, the flags that drive sending, and
 * the values it derives from an IP address. They are shared rather than
 * per-feature because the same list decides several different things —
 * whether an attribute renders with a "Reserved" badge, where it sorts in a
 * list of attributes, whether it can be deleted, and whether a CSV column may
 * be mapped onto it — and a copy that fell behind would answer one of those
 * questions differently from the others.
 */

/** A person's timezone, and whether Customer.io could resolve it. */
export const TIMEZONE = 'timezone';
export const TIMEZONE_VALID = 'timezone_valid';

/** When Customer.io first saw the person, as opposed to the customer's own `created_at`. */
export const CREATED_IN_CIO_AT = '_created_in_customerio_at';

export const TEST_USER = 'cio_test_user';
export const TEST_USER_DOUBLE_OPT_IN = 'cio_double_opt_in';

/** Per-person open-tracking consent, which the send path reads (AUD-3987). */
export const CIO_EMAIL_TRACKING_CONSENT = 'cio_email_tracking_consent';

export const RESTRICTED_ATTRIBUTES: readonly string[] = [
  'id',
  'email',
  CREATED_IN_CIO_AT,
  'anonymous_id',
  'created_at',
  'email_sha256',
  'mobile_ad_id',
  'mobile_ad_id_sha256',
  'unsubscribed',
  TEST_USER,
  TEST_USER_DOUBLE_OPT_IN,
  CIO_EMAIL_TRACKING_CONSENT,
  TIMEZONE,
  TIMEZONE_VALID,
];

/**
 * Whether the name is one Customer.io reserves.
 *
 * Asked of a mapping's *current* target rather than its saved one wherever the answer decides
 * what gets written: a column re-pointed at a reserved attribute in this session is as reserved
 * as one that was saved that way.
 */
export function isRestrictedAttribute(name: string | null | undefined): boolean {
  return name != null && RESTRICTED_ATTRIBUTES.includes(name);
}

/**
 * The reserved names a person's attribute can never be deleted under, and the
 * object equivalent. Narrower than `RESTRICTED_ATTRIBUTES`: restricted names
 * may not be *created* by hand, whereas these already exist on every record
 * and only ever change through Customer.io itself.
 */
export const PERMANENT_ATTRIBUTES: readonly string[] = [
  'cio_id',
  'id',
  'email',
  'created_at',
  CREATED_IN_CIO_AT,
  TIMEZONE,
  TIMEZONE_VALID,
];

export const PERMANENT_OBJECT_ATTRIBUTES: readonly string[] = [
  'cio_object_id',
  'object_id',
  'created_at',
  CREATED_IN_CIO_AT,
  'name',
  TIMEZONE,
  TIMEZONE_VALID,
];

/**
 * The attributes Customer.io derives from a person's IP address. They carry no
 * customer-authored meaning, so surfaces that explain where an attribute came
 * from label these as generated rather than describing them.
 */
export const CIO_GEO_ATTRIBUTES: readonly string[] = [
  'cio_iso_country',
  'cio_country',
  'cio_city',
  'cio_timezone',
  'cio_latitude',
  'cio_longitude',
  'cio_iso_continent',
  'cio_continent',
  'cio_iso_region',
  'cio_region',
  'cio_postal_code',
];

/** Reserved names on an event rather than a person. */
export const RESTRICTED_EVENT_ATTRIBUTES: readonly string[] = ['from_address', 'reply_to', 'recipient', 'form_submit'];

/**
 * Names an import hides from the mapping step until someone edits the row, so
 * they are not offered as ordinary columns. `id` is reserved as well; the name
 * this list adds on its own is the event timestamp.
 */
export const PROTECTED_ATTRIBUTES: readonly string[] = ['id', '_timestamp'];

/**
 * The three columns an event import needs, under the names the preprocessor
 * gives them. Everything else in an event row is its payload.
 */
export const REQUIRED_EVENT_IMPORT_ATTRIBUTES = {
  name: '_cio_name',
  customer_id: '_cio_customer_id',
  timestamp: '_cio_timestamp',
} as const;

/**
 * A JSON attribute's parsed payload: an object or array, never a scalar. The
 * payload is customer-authored free-form JSON, so `object` is the honest
 * contract — render it (stringify/format), don't reach into it.
 */
export type JSONAttributeValue = object;

/**
 * The parsed object/array behind a JSON attribute — either the value directly,
 * or a string that parses to one — or `undefined` when the value is not
 * JSON-like. Scalar-parsing strings like `"1"` or `"null"` are not JSON
 * attributes.
 */
export function parseJSONAttribute(value: unknown): JSONAttributeValue | undefined {
  if (value == null) return undefined;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return undefined;
  try {
    const parsed: unknown = JSON.parse(value);
    return parsed != null && typeof parsed === 'object' ? parsed : undefined;
  } catch {
    return undefined;
  }
}
